import {
  Component,
  OnInit,
  HostBinding,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy
} from '@angular/core';
import { LayoutService } from '@app/shared/services/layout.service';
import { TokenStorageService } from '@app/shared/services/token-storage.service';
import { ASidebarBuilder } from '../sidebar/builders/a-sidebar-builder';
import { SidebarMenuItem } from '../sidebar/models/sidebar-menu-item.model';
import { SidebarMenuContainer } from '../sidebar/models/sidebar-menu-container.model';
import { Router, NavigationEnd } from '@angular/router';
import { FtSidebarBuilder } from '../sidebar/builders/ft-sidebar-builder';
import { PeSidebarBuilder } from '../sidebar/builders/pe-sidebar-builder';
import { PpSidebarBuilder } from '../sidebar/builders/pp-sidebar-builder';
import { MtSidebarBuilder } from '../sidebar/builders/mt-sidebar-builder';
import { ProgramType } from '@app/shared/enums/program-type.enum';
import { CustomUtils } from '@shared/utils/custom-utils';
import { fromEvent, Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { SubnavState } from '@app/shared/enums/subnav-state.enum';
import { DeviceDetectorService } from 'ngx-device-detector';

@Component({
  selector: 'a360-subnav',
  templateUrl: './subnav.component.html',
  styleUrls: ['./subnav.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SubnavComponent implements OnInit, OnDestroy {
  private sidebarBuilder: ASidebarBuilder;

  @Input() forceOpen = false;
  @Output() subnavClosed = new EventEmitter<void>();
  @Output() programSwitcherRequested = new EventEmitter<boolean>();

  public get programSelectorContent(): Array<SidebarMenuItem> {
    return this.sidebarBuilder?.programSelectorContent || [];
  }
  public get programMenuContent(): Array<SidebarMenuItem | SidebarMenuContainer> {
    return this.sidebarBuilder?.programMenuContent || [];
  }
  public get systemMenuContent(): Array<SidebarMenuItem | SidebarMenuContainer> {
    return this.sidebarBuilder?.systemMenuContent || [];
  }
  public get utilsMenuContent(): Array<SidebarMenuItem | SidebarMenuContainer> {
    return this.sidebarBuilder?.utilsMenuContent || [];
  }
  public get platformTopNavEnabled(): boolean {
    return this.tokenStorageService.userInfo?.CurrentSite.PlatformTopNavEnabled ?? false;
  }
  public isMobile = false;
  public verticalNavItems: any[] = [];
  private readonly subscriptions = new Subscription();
  private readonly timers: number[] = [];
  public window = window;
  public expandedParents = new Set<number>();
  private userExpandedOnMediumScreen: boolean;
  private userCollapsedOnLargeScreen: boolean;
  public isProgramSwitcherVisible = false;
  private lastAccessedSubnavItem: string;
  private isInitialLoad = true;

  constructor(
    private readonly tokenStorageService: TokenStorageService,
    public readonly layoutService: LayoutService,
    private readonly router: Router,
    private readonly ftSidebarBuilder: FtSidebarBuilder,
    private readonly peSidebarBuilder: PeSidebarBuilder,
    private readonly ppSidebarBuilder: PpSidebarBuilder,
    private readonly mtSidebarBuilder: MtSidebarBuilder,
    private readonly deviceDetectorService: DeviceDetectorService
  ) {}

  private focusLastNavigatedItem(): void {
    const timer = window.setTimeout(() => {
      const navItem = document.getElementById('subnav-item-' + this.lastAccessedSubnavItem) as HTMLElement;
      if (navItem) {
        navItem.focus();
      }
    }, 300);
    this.timers.push(timer);
  }

  private handleFocusReturnToTrigger(): void {
    const timer1 = window.setTimeout(() => {
      const triggerButton = document.getElementById('program-switcher-trigger');
      if (triggerButton) {
        triggerButton.focus();
        triggerButton.classList.add('keyboard-focused');
      }
      const timer2 = window.setTimeout(() => {
        this.layoutService.focusAfterProgramChange = false;
      }, 500);
      this.timers.push(timer2);
    }, 500);
    this.timers.push(timer1);
  }

  public ngOnInit(): void {
    const routerEventsSub = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.initSidebarBuilder();
        this.verticalNavItems = this.buildVerticalNavItems();
        this.expandParentsOfActiveItems();

        if (this.layoutService.focusAfterProgramChange) {
          this.handleFocusReturnToTrigger();
        } else if (this.isInitialLoad) {
          this.isInitialLoad = false;
        } else {
          this.focusLastNavigatedItem();
        }
      }
    });
    this.subscriptions.add(routerEventsSub);

    this.layoutService.subnavState = SubnavState.none;
    this.setSubNav();

    const resizeSub = fromEvent(window, 'resize').subscribe(() => {
      this.setSubNav();
    });
    this.subscriptions.add(resizeSub);

    if (this.tokenStorageService.userInfo) {
      this.initSidebarBuilder();
      this.verticalNavItems = this.buildVerticalNavItems();
      this.expandParentsOfActiveItems();
    }
    const showProgramSwitcherSub = this.layoutService.showProgramSwitcher$.pipe(skip(1)).subscribe((val) => {
      this.isProgramSwitcherVisible = val;
      const timer = window.setTimeout(() => {
        const triggerButton = document.getElementById('program-switcher-trigger');
        if (triggerButton) {
          if (this.isProgramSwitcherVisible) {
            triggerButton.classList.add('selected');
          } else {
            triggerButton.classList.remove('selected');
          }
        }
        if (!this.isProgramSwitcherVisible) {
          triggerButton?.focus();
        }
      }, 100);
      this.timers.push(timer);
    });
    this.subscriptions.add(showProgramSwitcherSub);

    const userInfoSub = this.tokenStorageService.userInfoChanged.subscribe(() => {
      this.expandedParents.clear();
    });
    this.subscriptions.add(userInfoSub);
  }

  public getProgramInfoAriaLabel(): string {
    const action = 'Change Program.';
    const currentProgram = `Current program is: ${this.currentProgramName}, ${this.currentProgramType}.`;
    return `${action} ${currentProgram}`;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.timers.forEach((timer) => clearTimeout(timer));
    this.timers.length = 0;
    this.expandedParents.clear();
    this.sidebarBuilder?.cleanup();
    this.sidebarBuilder = null;
    this.verticalNavItems = [];
  }

  public get showSubnav(): boolean {
    return this.layoutService.subnavState === SubnavState.full;
  }
  public get subnavCollapsed(): boolean {
    return this.layoutService.subnavState === SubnavState.collapsed;
  }
  public get subnavHidden(): boolean {
    return this.layoutService.subnavState === SubnavState.hidden;
  }

  private getAutId(item: any): string {
    const str = item?.name || item?.label || '';
    return str.trim().replace(/\s+/g, '-').toLowerCase();
  }

  private setSubNav(): void {
    if (this.isMobile && this.layoutService.subnavState === SubnavState.full) {
      this.layoutService.subnavState = SubnavState.hidden;
    }
    this.isMobile = window.innerWidth < 768;
    if (this.isMobile) {
      this.layoutService.subnavState = SubnavState.hidden;
      this.userExpandedOnMediumScreen = false;
      this.userCollapsedOnLargeScreen = false;
    } else if (window.innerWidth < 1024) {
      if (this.userExpandedOnMediumScreen === true) {
        this.layoutService.subnavState = SubnavState.full;
      } else {
        this.layoutService.subnavState = SubnavState.collapsed;
      }
      this.userCollapsedOnLargeScreen = false;
    } else {
      this.layoutService.subnavState = this.userCollapsedOnLargeScreen ? SubnavState.collapsed : SubnavState.full;
      this.userExpandedOnMediumScreen = false;
    }
  }
  private initSidebarBuilder(): void {
    const userInfo = this.tokenStorageService.userInfo;
    if (!userInfo) {
      this.sidebarBuilder = null;
      return;
    }
    if (!userInfo.CurrentSiteId || !userInfo.CurrentProgramId) {
      this.sidebarBuilder = this.ftSidebarBuilder;
    } else {
      switch (userInfo.CurrentProgram.Type) {
        case ProgramType.FieldTraining:
          this.sidebarBuilder = this.ftSidebarBuilder;
          break;
        case ProgramType.PerformanceEvaluation:
          this.sidebarBuilder = this.peSidebarBuilder;
          break;
        case ProgramType.ProbationaryPeriod:
          this.sidebarBuilder = this.ppSidebarBuilder;
          break;
        case ProgramType.Mentoring:
          this.sidebarBuilder = this.mtSidebarBuilder;
          break;
        default:
          this.sidebarBuilder = null;
      }
    }
    if (this.sidebarBuilder) {
      this.sidebarBuilder.rebuild(userInfo);
    }
  }

  public toggleCollapse(): void {
    if (this.isMobile) return;
    if (window.innerWidth >= 768 && window.innerWidth < 1024) {
      this.userExpandedOnMediumScreen = true;
    }
    if (window.innerWidth >= 1024) {
      this.userCollapsedOnLargeScreen = true;
    }
    this.layoutService.subnavState =
      this.layoutService.subnavState === SubnavState.collapsed ? SubnavState.full : SubnavState.collapsed;
  }

  public onVerticalNavItemClick(item: any, parentIdx?: number): void {
    if (item?.action) {
      item.action();
      this.lastAccessedSubnavItem = item.label;
    }
    if (typeof parentIdx === 'number') {
      if (this.expandedParents.has(parentIdx)) {
        this.expandedParents.delete(parentIdx);
      } else {
        this.expandedParents.add(parentIdx);
      }
    }

    if (this.isMobile) {
      this.layoutService.subnavState = SubnavState.hidden;
    }
  }

  private buildVerticalNavItems(): any[] {
    const flatten = (items: Array<SidebarMenuItem | SidebarMenuContainer>) =>
      items
        .filter((item) => {
          return typeof item['hidden'] === 'function' ? !item['hidden']() : true;
        })
        .map((item) =>
          item instanceof SidebarMenuContainer
            ? { label: item.name, children: flatten(item.sublings), autId: this.getAutId(item) }
            : {
                label: item.name,
                action: item.action,
                isActive: item.isActive ? item.isActive.bind(item) : () => false,
                autId: this.getAutId(item)
              }
        );

    if (!this.deviceDetectorService.isDesktop()) {
      const filteredSystemMenu = this.programMenuContent.filter((item) => {
        const name = (item['name'] || item['label'] || '').trim().toLowerCase();
        return name !== 'reports';
      });
      return flatten(filteredSystemMenu);
    }
    return [...flatten(this.programMenuContent), ...flatten(this.systemMenuContent)];
  }

  public isMenuItemActive(item: any): boolean {
    return item.isActive?.();
  }

  public isAnyChildActive(item: any): boolean {
    if (!item.children) return false;
    return item.children?.some((child) => child.isActive?.() || this.isAnyChildActive(child)) ?? false;
  }

  @HostBinding('class.disabled')
  public get disabled(): boolean {
    return this.layoutService.barsDisabled;
  }

  @HostBinding('class.mobile') get hostMobileClass() {
    return this.isMobile;
  }

  public get currentProgramName(): string {
    return this.tokenStorageService.userInfo?.CurrentProgramName;
  }

  public get currentProgramType(): string {
    const type = this.tokenStorageService.userInfo.CurrentProgram?.Type;
    return type ? CustomUtils.getProgramType(type) : '';
  }

  public closeSubnav(): void {
    if (this.isMobile) {
      this.subnavClosed.emit();
      const timer = window.setTimeout(() => this.layoutService.adjustLayoutForSubnav(), 0);
      this.timers.push(timer);
    }
  }
  public onProgramInfoClick(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (this.layoutService.showProgramSwitcher) {
      this.layoutService.showProgramSwitcher = false;
    } else {
      this.layoutService.showProgramSwitcher = true;
    }
  }

  public isParentExpanded(idx: number): boolean {
    return this.expandedParents.has(idx);
  }

  private expandParentsOfActiveItems(): void {
    this.verticalNavItems.forEach((item, idx) => {
      if (item.children?.some((child) => this.isMenuItemActive(child)) && !this.expandedParents.has(idx)) {
        this.expandedParents.add(idx);
      }
    });
  }
}
